import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, routeMapping } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { founderData } from '../data/mockData';
import SEOHead from '../components/SEOHead';

const FounderBilingual = () => {
  const { language, t } = useLanguage();
  const founder = translations.founder;
  const nav = translations.nav;
  
  const langPrefix = `/${language}`;
  const getRoute = (pageKey) => {
    const route = routeMapping[language][pageKey];
    return `${langPrefix}${route ? '/' + route : ''}`;
  };

  const experiences = [
    { category: founder.exp1Category, items: founder.exp1Items },
    { category: founder.exp2Category, items: founder.exp2Items },
    { category: founder.exp3Category, items: founder.exp3Items },
    { category: founder.exp4Category, items: founder.exp4Items },
    { category: founder.exp5Category, items: founder.exp5Items },
    { category: founder.exp6Category, items: founder.exp6Items }
  ];

  const principles = [
    { title: founder.principle1Title, desc: founder.principle1Desc },
    { title: founder.principle2Title, desc: founder.principle2Desc },
    { title: founder.principle3Title, desc: founder.principle3Desc },
    { title: founder.principle4Title, desc: founder.principle4Desc }
  ];

  return (
    <div>
      <SEOHead 
        title={t(founder.seoTitle)}
        description={t(founder.seoDescription)}
      />
      
      {/* Hero Section */}
      <section className="section-padding-large" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div className="grid-two-column" style={{ gap: '80px', alignItems: 'center' }}>
            <div>
              <p className="meta-text" style={{ marginBottom: '24px', color: 'var(--gold-accent)' }}>
                {t(founder.heroSubtitle)}
              </p>
              <h1 className="hero-large" style={{ marginBottom: '16px' }}>
                Nidale Barret
              </h1>
              <p className="heading-3" style={{ 
                marginBottom: '32px',
                color: 'var(--gold-accent)'
              }}>
                {t(founder.heroTitle)}
              </p>
              <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
                {t(founder.heroDescription)}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img 
                src={founderData.image}
                alt="Nidale Barret"
                style={{
                  width: '100%',
                  maxWidth: '450px',
                  height: 'auto',
                  objectFit: 'contain',
                  filter: 'grayscale(100%) contrast(1.2) brightness(0.95)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
                {t(founder.journeySubtitle)}
              </p>
              <h2 className="heading-1">
                {t(founder.journeyTitle)}
              </h2>
            </div>
            
            <div style={{ marginBottom: '60px' }}>
              <h3 className="heading-2" style={{ marginBottom: '32px' }}>
                {t(founder.experienceTitle)}
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {experiences.map((exp, idx) => (
                  <div key={idx} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '24px',
                    paddingBottom: '24px',
                    borderBottom: idx < experiences.length - 1 ? '1px solid var(--border-light)' : 'none'
                  }}>
                    <span className="meta-text" style={{ 
                      color: 'var(--gold-accent)',
                      minWidth: '24px'
                    }}>
                      0{idx + 1}
                    </span>
                    <div>
                      <p className="body-regular" style={{ 
                        color: 'var(--text-secondary)',
                        marginBottom: '4px'
                      }}>
                        {t(exp.category)}
                      </p>
                      <p className="heading-3" style={{ fontSize: '16px' }}>
                        {t(exp.items)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="heading-2" style={{ marginBottom: '24px' }}>
                {t(founder.perspectiveTitle)}
              </h3>
              <p className="body-large" style={{ 
                marginBottom: '24px',
                color: 'var(--text-secondary)',
                lineHeight: 1.8
              }}>
                {t(founder.perspectiveP1)}
              </p>
              <p className="body-large" style={{ 
                color: 'var(--text-secondary)',
                lineHeight: 1.8
              }}>
                {t(founder.perspectiveP2)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Guiding Principles Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
              {t(founder.principlesSubtitle)}
            </p>
            <h2 className="heading-1">
              {t(founder.principlesTitle)}
            </h2>
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(400px, 100%), 1fr))',
            gap: '48px',
            maxWidth: '1000px',
            margin: '0 auto'
          }}>
            {principles.map((principle, idx) => (
              <div key={idx}>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {t(principle.title)}
                </h3>
                <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
                  {t(principle.desc)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="section-padding" style={{ background: 'var(--bg-secondary)' }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <p className="meta-text" style={{ marginBottom: '16px', color: 'var(--gold-accent)' }}>
              {t(founder.visionSubtitle)}
            </p>
            <h2 className="heading-1" style={{ marginBottom: '32px' }}>
              {t(founder.visionTitle)}
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '40px',
              color: 'var(--text-secondary)',
              lineHeight: 1.8
            }} dangerouslySetInnerHTML={{ __html: t(founder.visionP1) }} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <div style={{ maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
            <h2 className="heading-1" style={{ marginBottom: '24px' }}>
              {t(founder.ctaTitle)}
            </h2>
            <p className="body-large" style={{ 
              marginBottom: '40px',
              color: 'var(--text-secondary)'
            }} dangerouslySetInnerHTML={{ __html: t(founder.ctaDescription) }} />
            <Link to={getRoute('contact')} className="btn-primary" data-testid="cta-btn">
              {t(nav.cta)}
            </Link>
          </div>
        </div>
      </section>

      {/* Micro-SEO Block */}
      <section style={{ padding: '40px 0', background: 'var(--bg-secondary)' }}>
        <div className="container">
          <p style={{ 
            fontSize: '12px',
            lineHeight: 1.7,
            color: 'var(--text-light)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {t(founder.seoBlock)}
          </p>
        </div>
      </section>
    </div>
  );
};

export default FounderBilingual;
